import {
  HbrBadge,
  HbrOption,
  HbrSelect,
  HbrSpinner,
} from '@harbor/elements-react-standalone';
import React, { useEffect, useMemo, useState } from 'react';
import { useUserRegistry } from '../../../hooks/useUserRegistry';
import { Column, DataType, getColumnTypeConfig } from '../../../types/table';
import { Filter, UserView } from '../../../types/user';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import StbTableFilterValueControl from './StbTableFilterValueControl';
import {
  baseOperators,
  dateOperators,
  numberOperators,
  stringOperators,
} from './table-constants';
import { Operator } from './table-types';
import { valueControlOverride } from './custom-column-filters';
import { asSingleValue } from '../../../support/utils';


interface Props {
  userView: UserView;
  visibleColumns?: Column[]; // required for updates if users can toggle columns. Otherwise userView is enough
  setViewFilters: (filters: Filter[]) => void;
  columnIdOverrides?: { [key: string]: string }; // use when column ids don't match the related api property name
}

const StbTableFilters = ({ userView, setViewFilters, visibleColumns, columnIdOverrides }: Props) => {
  const { updateViewFilters } = useUserRegistry();
  const [filters, setFilters] = useState<Filter[]>([]);
  const [dirty, setDirty] = useState<boolean>(false); 

  useEffect(() => {
    if (!userView) return;
    const fetchedFilters = userView?.filters ?? [];
    if (fetchedFilters) {
      setFilters(fetchedFilters);
    }
  }, [userView]);

  const columns: Column[] = useMemo(
    () => {
      return visibleColumns?.filter(c => c.id && c.name) ?? 
      (userView?.columns ?? []).map((col) => getColumnTypeConfig(col))
    },
    [visibleColumns, userView]
  );

  const applyFilters = (currentFilters: Filter[]) => {
    setDirty(false);
    updateViewFilters(userView.type, userView.id, currentFilters); // save to registry

    // transform to values that the api can handle
    const apiFilters = currentFilters.map((filter: Filter) => {
      const columnConfig = findColumnConfig(filter.prop);
      const columnType = columnConfig.columnType;
      const newFilter = {...filter};

      if (columnType === 'date') {
        // date uses YYYY-MM-DD
        newFilter.comparison = newFilter.comparison?.substring(0, 10);
      }

      return newFilter;
    });
    setViewFilters(apiFilters); // apply to parent table
  };

  const changeFilters = (currentFilters: Filter[]) => {
    setDirty(true);
    setFilters(currentFilters);
  }

  const getOperatorType = (filter: Filter) => {
    const column = findColumnConfig(filter.prop);
    const operator = getOperators(column)[filter.operator];
    return operator?.control;
  };

  const getFilterOperatorOptions = (filter) => {
    const column = findColumnConfig(filter.prop);
    return (
      <>
        {Object.values(getOperators(column))?.map((operator) => (
          <HbrOption key={`operator-option-${operator.id}`} value={operator.id}>
            {operator.name}
          </HbrOption>
        ))}
      </>
    );
  };

  const handleFilterPropChange = (filterIndex: number, value: string) => {
    const filters = changedFilters(filterIndex, 'prop', value);
    delete filters[filterIndex].operator;
    delete filters[filterIndex].comparison;
    changeFilters(filters);
  };

  const handleFilterOperatorChange = (filterIndex: number, value: string) => {
    const updatedFilters = changedFilters(filterIndex, 'operator', value);
    const filter = updatedFilters[filterIndex];
    delete filter.comparison;
    if (getOperatorType(filter) === 'multi') {
      filter.comparison = [''];
    }
    changeFilters(updatedFilters);
  };

  const handleFilterComparisonChange = (
    filterIndex: number,
    value: string | number | string[]
  ) => {
    changeFilters(changedFilters(filterIndex, 'comparison', value));
  };

  const handleDeleteFilter = (filterIndex: number) => {
    const newFilters = [...filters];
    newFilters.splice(filterIndex, 1);
    changeFilters(newFilters);
  };

  const findColumnConfig = (id: string): Column => {
    return columns.find((c) => c.id === id);
  };

  const getDataTypeOperators = (
    datatype: DataType
  ): { [key: string]: Operator } => {
    switch (datatype) {
      case DataType.Number:
        return numberOperators;
      case DataType.String:
        return stringOperators;
      case DataType.Date:
        return dateOperators;
      default:
        return {};
    }
  };

  const getOperators = (column: Column): { [key: string]: Operator } => ({
    ...baseOperators,
    ...getDataTypeOperators(column?.dataType ?? DataType.String),
  });

  const changedFilters = (
    filterIndex: number,
    key: string,
    value: string | number | string[]
  ): Filter[] => {
    const newFilters = [...filters];
    newFilters[filterIndex] = {
      ...filters[filterIndex],
      [key]: value ?? filters[filterIndex][key],
    };
    return newFilters;
  };

  const customValueControl = (filter: Filter, handler): React.ReactNode | null => {
    if (['truthy', 'falsy'].includes(filter.operator)) {
      return <></>;
    }
    const opType = getOperatorType(filter);
    return valueControlOverride(filter.prop, opType, handler, filter.comparison);
  }

  const columnIsVisible = (columnId: string): boolean => {
    return !!visibleColumns.find(c => (columnIdOverrides?.[c.id] ?? c.id) === columnId || c.id === columnId);
  }

  const displayName = (propName: string): string => {
    if (columnIdOverrides) {
      const keys = Object.keys(columnIdOverrides ?? {});
      const display = keys.find(k => columnIdOverrides[k] === propName);
      if (display) {
        return display;
      }
    }
    return visibleColumns.find(c => c.id === propName)?.name ?? propName;
  }

  if (!userView) {
    return <HbrSpinner size='medium' />;
  }

  return (
    <div className='max-w-2xl'>
      <StbAccordion
        headerContent={
          <div className='flex gap-1 items-center'>
            <h4 className={`flex-1 my-0 mr-2`}>Filters</h4>
            <HbrBadge
              slot='suffix'
              pill
              size='small'
              sentiment={filters?.length ? 'info' : 'neutral'}
              className='-my-2'
            >
              {filters?.length ?? 0}
            </HbrBadge>
          </div>
        }
      >
        {(filters ?? [{}])?.map((filter: Filter, n: number) => (
          <div
            className={`grid grid-cols-[1fr_2rem] gap-1 border-t last:border-b py-2 pl-4 pr-1 border-gray-500 flex-0`}
            key={`filter-row-${n}`}
          >
            <div className='grid gap-1 grid-cols-[2fr_9rem] md:grid-cols-[2fr_9rem_3fr]'>
              <div className='flex-1'>
                <HbrSelect
                  value={filter.prop}
                  placeholder='Property'
                  error={!columnIsVisible(filter.prop)}
                  onHbr-change={(e) =>
                    handleFilterPropChange(n, asSingleValue(e.target.value))
                  }
                >
                  {!columnIsVisible(filter.prop) && (
                    <HbrOption
                      value={filter.prop}
                      key={`filter-prop-option-${n}`}
                    >
                      {filter.prop}
                    </HbrOption>
                  )}
                  {columns?.map((column, n) => (
                    <HbrOption
                      value={columnIdOverrides?.[column.id] ?? column.id}
                      key={`filter-prop-option-${n}`}
                    >
                      {displayName(column.id)}
                    </HbrOption>
                  ))}
                </HbrSelect>
              </div>
              <div className='flex-0 w-36 mr-2'>
                {!!filter.prop && (
                  <HbrSelect
                    value={filter.operator}
                    placeholder='Operator'
                    onHbr-change={(e) =>
                      handleFilterOperatorChange(n, asSingleValue(e.target.value))
                    }
                  >
                    {getFilterOperatorOptions(filter)}
                  </HbrSelect>
                )}
              </div>
              <div className='flex-1 col-span-2 md:col-span-1'>
                {!!filter.operator && (
                  customValueControl(filter, value => handleFilterComparisonChange(n, value))
                  || <StbTableFilterValueControl
                    filterIndex={n}
                    handleFilterComparisonChange={handleFilterComparisonChange}
                    operatorType={getOperatorType(filter)}
                    filterValue={filter.comparison}
                  />
                )}
              </div>
            </div>
            <div className='flex items-center min-h-0 h-full'>
              <StbButton
                role="delete"
                iconOnly
                variant="text"
                onClick={handleDeleteFilter.bind(null, n)}
              />
            </div>
          </div>
        ))}
        <footer className="mx-4 mb-2 flex justify-between">
          <StbButton
            role="add"
            onClick={(e) => {
              e.stopPropagation();
              changeFilters([
                ...filters,
                { prop: columns[0].id, operator: 'is', comparison: '' },
              ]);
            }}
          >
            Add Rule
          </StbButton>
          <StbButton
            role="save"
            onClick={() => applyFilters(filters)}
            disabled={!dirty}
          >
            Apply Filters
          </StbButton>
        </footer>
      </StbAccordion>
    </div>
  );
};

export default StbTableFilters;
